import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import inventoryItemService, { extractInventoryError } from '../../../../services/inventoryItemService';
import inventoryMovementService from '../../../../services/inventoryMovementService';
import inventoryBalanceService from '../../../../services/inventoryBalanceService';
import InventoryQuantityBadge from '../../../../components/venue/InventoryQuantityBadge';
import type { InventoryBalance, InventoryItem } from '../../../../types/venue';

type MovementTypeOption = 'MANUAL_ADJUSTMENT' | 'WASTE';

const TYPE_LABELS: Record<MovementTypeOption, string> = {
  MANUAL_ADJUSTMENT: 'Adjustment',
  WASTE: 'Waste / Spoilage',
};

const ManualAdjustmentForm: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { venueId } = useVenue();

  const preselectedItemId = searchParams.get('itemId') ?? '';
  const backPath = preselectedItemId
    ? `/admin/venues/${venueId}/inventory/${preselectedItemId}`
    : `/admin/venues/${venueId}/inventory`;

  // ── Context item + current balance ────────────────────────────────────────────
  const [contextItem, setContextItem] = useState<InventoryItem | null>(null);
  const [currentBalance, setCurrentBalance] = useState<InventoryBalance | null>(null);
  const [contextLoading, setContextLoading] = useState(false);

  useEffect(() => {
    if (!venueId || !preselectedItemId) return;
    setContextLoading(true);

    Promise.all([
      inventoryItemService.getItem(venueId, preselectedItemId),
      inventoryBalanceService.getBalance(venueId, preselectedItemId).catch(() => null),
    ])
      .then(([item, balance]) => {
        setContextItem(item);
        setCurrentBalance(balance);
      })
      .catch(() => { /* non-blocking */ })
      .finally(() => setContextLoading(false));
  }, [venueId, preselectedItemId]);

  // Prefill unitCost from last known cost once balance loads
  useEffect(() => {
    if (currentBalance?.lastCost != null) {
      setUnitCost(String(currentBalance.lastCost));
    }
  }, [currentBalance]);

  // ── Form state ────────────────────────────────────────────────────────────────
  const [movementType, setMovementType] = useState<MovementTypeOption>('MANUAL_ADJUSTMENT');
  const [direction, setDirection] = useState<'in' | 'out'>('in');
  const [quantityInput, setQuantityInput] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [notes, setNotes] = useState('');

  // ── Submission ────────────────────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  // ── Derived values ────────────────────────────────────────────────────────────
  const quantityNum = quantityInput !== '' ? Number(quantityInput) : null;
  const quantityError = touched && (quantityNum === null || quantityNum <= 0 || !Number.isInteger(quantityNum));

  const isOut = direction === 'out' || movementType === 'WASTE';
  const signedQuantity =
    quantityNum != null ? (isOut ? -Math.abs(quantityNum) : Math.abs(quantityNum)) : 0;

  const predictedBalance =
    currentBalance != null && signedQuantity !== 0
      ? currentBalance.quantity + signedQuantity
      : null;

  // ── Submit ────────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setTouched(true);
    if (quantityNum === null || quantityNum <= 0 || !Number.isInteger(quantityNum)) return;
    if (!preselectedItemId) return;

    setSubmitting(true);
    setError(null);
    try {
      await inventoryMovementService.postManualMovement(venueId, {
        inventoryItemId: preselectedItemId,
        type: movementType,
        quantity: signedQuantity,
        ...(unitCost !== '' && { unitCost: Number(unitCost) }),
        ...(notes.trim() && { notes: notes.trim() }),
      });
      // Navigate back to detail — it will reload the balance fresh on mount
      navigate(backPath);
    } catch (err) {
      setError(extractInventoryError(err));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3 }}>
      <Paper
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          maxWidth: 600,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          }}
        >
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate(backPath)}
            sx={{ textTransform: 'none', color: theme.palette.text.secondary }}
          >
            {preselectedItemId ? 'Item Detail' : 'Inventory'}
          </Button>
          <Divider orientation="vertical" flexItem />
          <Typography variant="h6" fontWeight={600}>Manual Adjustment</Typography>
        </Box>

        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

          {!preselectedItemId && (
            <Alert severity="info">
              Navigate to an inventory item and use the Adjust button to post a movement.
            </Alert>
          )}

          {/* Context card */}
          {contextLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={14} />
              <Typography variant="body2" color="text.secondary">Loading item…</Typography>
            </Box>
          )}

          {contextItem && (
            <Box
              sx={{
                p: 2,
                borderRadius: 1.5,
                backgroundColor: alpha(theme.palette.primary.main, 0.06),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>
                  {contextItem.wineDisplay?.name ?? 'Unknown Wine'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {contextItem.wineDisplay?.producerName ?? ''}
                </Typography>
              </Box>
              {currentBalance != null && (
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary">Current stock</Typography>
                  <Box>
                    <InventoryQuantityBadge
                      quantity={currentBalance.quantity}
                      parLevel={contextItem.parLevel}
                      reorderPoint={contextItem.reorderPoint}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Movement type */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Movement Type</Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={movementType}
                label="Type"
                onChange={e => setMovementType(e.target.value as MovementTypeOption)}
                disabled={!preselectedItemId}
              >
                {(Object.keys(TYPE_LABELS) as MovementTypeOption[]).map(t => (
                  <MenuItem key={t} value={t}>{TYPE_LABELS[t]}</MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {movementType === 'WASTE'
                  ? 'Waste is always stock-out (negative quantity).'
                  : 'Adjustment can add or remove stock.'}
              </FormHelperText>
            </FormControl>
          </Box>

          {/* Direction — only for MANUAL_ADJUSTMENT */}
          {movementType === 'MANUAL_ADJUSTMENT' && (
            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Direction</Typography>
              <ToggleButtonGroup
                exclusive
                value={direction}
                onChange={(_, val) => { if (val) setDirection(val); }}
                size="small"
                disabled={!preselectedItemId}
              >
                <ToggleButton value="in" sx={{ textTransform: 'none', fontWeight: 600, px: 3 }}>
                  + Stock In
                </ToggleButton>
                <ToggleButton value="out" sx={{ textTransform: 'none', fontWeight: 600, px: 3 }}>
                  − Stock Out
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          {/* Quantity */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Quantity (bottles)</Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Quantity"
              value={quantityInput}
              onChange={e => setQuantityInput(e.target.value)}
              inputProps={{ min: 1, step: 1 }}
              disabled={!preselectedItemId}
              error={quantityError}
              helperText={
                quantityError
                  ? 'Enter a positive whole number.'
                  : signedQuantity !== 0
                  ? `Will post ${signedQuantity > 0 ? '+' : ''}${signedQuantity} to the ledger.`
                  : 'Enter the number of bottles.'
              }
            />
          </Box>

          {/* Predicted balance preview */}
          {predictedBalance !== null && (
            <Box
              sx={{
                p: 2,
                borderRadius: 1.5,
                backgroundColor: alpha(
                  predictedBalance < 0 ? theme.palette.warning.main : theme.palette.success.main,
                  0.07,
                ),
                border: `1px solid ${alpha(
                  predictedBalance < 0 ? theme.palette.warning.main : theme.palette.success.main,
                  0.2,
                )}`,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">Expected balance after posting</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <InventoryQuantityBadge
                    quantity={predictedBalance}
                    parLevel={contextItem?.parLevel}
                    reorderPoint={contextItem?.reorderPoint}
                  />
                </Box>
              </Box>
              {predictedBalance < 0 && (
                <Typography variant="caption" color="warning.dark">
                  Negative balance is allowed — adjust your stock count if needed.
                </Typography>
              )}
            </Box>
          )}

          {/* Unit cost (optional) */}
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Unit Cost (optional)"
            value={unitCost}
            onChange={e => setUnitCost(e.target.value)}
            inputProps={{ min: 0, step: 0.01 }}
            disabled={!preselectedItemId}
            helperText="Cost per bottle — updates last cost on balance."
          />

          {/* Notes */}
          <TextField
            fullWidth
            size="small"
            label="Notes / Reason"
            multiline
            rows={3}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            inputProps={{ maxLength: 2000 }}
            disabled={!preselectedItemId}
            helperText="Visible in the movement ledger."
          />

          {/* Ledger warning */}
          <Box
            sx={{
              p: 2,
              backgroundColor: alpha(theme.palette.warning.main, 0.08),
              borderRadius: 1,
              border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
            }}
          >
            <Typography variant="body2" color="warning.dark">
              This posts a permanent entry to the <strong>append-only movement ledger</strong>.
              It cannot be deleted — only corrected with a reverse adjustment.
            </Typography>
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
            <Button onClick={() => navigate(backPath)} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleSubmit}
              disabled={submitting || !preselectedItemId}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {submitting ? 'Posting…' : 'Post Adjustment'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default ManualAdjustmentForm;
